import React, { createContext, useContext, useTransition, useCallback } from 'react';
import { useNavigate as useRRNavigate } from 'react-router-dom';

const NavigationContext = createContext({ isPending: false, startTransition: () => {} });

/**
 * Provides React 18 useTransition-based navigation.
 * Wrapping route changes in startTransition tells React to keep the old UI
 * visible while the new lazy-loaded page downloads — no flash / spinner.
 */
export function NavigationProvider({ children }) {
  const [isPending, startTransition] = useTransition();

  return (
    <NavigationContext.Provider value={{ isPending, startTransition }}>
      {children}
    </NavigationContext.Provider>
  );
}

/**
 * Drop-in replacement for useNavigate().
 * Wraps every navigation in startTransition so the old page stays
 * on screen until the new chunk is ready.
 */
export function useAppNavigate() {
  const navigate = useRRNavigate();
  const { startTransition } = useContext(NavigationContext);

  return useCallback((...args) => {
    startTransition(() => {
      navigate(...args);
    });
  }, [navigate, startTransition]);
}

/** Returns true while a transition-based navigation is in flight. */
export function useNavigationPending() {
  return useContext(NavigationContext).isPending;
}

/** Raw access (used by AppLink). */
export function useNavigationContext() {
  return useContext(NavigationContext);
}

export default NavigationContext;
